import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getStaffReturnPath, getStaffSection } from '../../../../app/router/staffNavigation';
import { auditService, AuditHistoryList, IAuditRegularScheduleHistoryItem } from "../../../../entities/audit";
import { useStaffFeedback } from "../../model/useStaffFeedback";
import { useStaffMember } from "../../model/useStaffMember";
import { StaffDetailLayout } from "../staffDetail/StaffDetailLayout";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import { studentService } from "../../../../entities/student";
import { DropDownListSelected } from "../../../../shared/ui/dropDownSelected";
import { IItem } from "../../../../shared/model/types";
import staffClasses from "../staff/staffWidget.module.scss";
import { FEATURES, hasFeature } from "../../../../entities/my";
import { useAppSelector } from "../../../../app/store/store";

const TUTOR_HISTORY_FILTER_ID = 0

export const StaffHistoryWidget: FC = () => {
    const {my} = useAppSelector(state => state.myReducer)
    const navigate = useNavigate()
    const {pathname, search} = useLocation()
    const backPath = getStaffReturnPath(search, getStaffSection(pathname))
    const {user_id} = useParams<{user_id: string}>()
    const staffId = user_id && !Number.isNaN(Number(user_id)) ? Number(user_id) : null
    const {handleError, showError} = useStaffFeedback()
    const [items, setItems] = useState<IAuditRegularScheduleHistoryItem[]>([])
    const [isHistoryLoading, setIsHistoryLoading] = useState(false)
    const [studentItems, setStudentItems] = useState<IItem<number>[]>([])
    const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([])
    const [isStudentsLoading, setIsStudentsLoading] = useState(false)
    const onNotFound = useCallback(() => {
        navigate(backPath, {replace: true})
        showError('Сотрудник не найден')
    }, [backPath, navigate, showError])

    const {
        isLoading,
        loadError,
        staffMember,
    } = useStaffMember({staffId, onError: handleError, onNotFound})

    const canReadScheduleHistory = staffId !== null && hasFeature(my, FEATURES.AUDIT.SCHEDULE_HISTORY_READ, staffId)
    const isTutorStaff = staffMember.roles.some(role => role.role_code.trim().toLowerCase() === 'tutor')
    const hasStudentFilter = isTutorStaff

    const historyFilterItems = useMemo<IItem<number>[]>(() => [
        {id: TUTOR_HISTORY_FILTER_ID, name: '(Исполнитель)'},
        ...studentItems,
    ], [studentItems])

    const onSelectedStudent = useCallback((item: IItem<number>) => {
        return (selected: boolean) => {
            setSelectedStudentIds(prev => (
                selected
                    ? (prev.includes(item.id) ? prev : [...prev, item.id])
                    : prev.filter(id => id !== item.id)
            ))
        }
    }, [])

    const onSelectAllStudents = useCallback((ids: number[]) => {
        setSelectedStudentIds(ids)
    }, [])

    const selectedStudentItems = useMemo(() => (
        historyFilterItems.filter(item => selectedStudentIds.includes(item.id))
    ), [historyFilterItems, selectedStudentIds])

    useEffect(() => {
        if(!staffId || isLoading || !isTutorStaff || !canReadScheduleHistory){
            setStudentItems([])
            setSelectedStudentIds([])
            return
        }

        let isActual = true
        setIsStudentsLoading(true)

        const loadStudents = async () => {
            try{
                let response: {students: {id: number; first_name: string; last_name: string; middle_name: string;}[]}

                try{
                    response = await studentService.getAllByTutorUserIdForAdmin(staffId)
                }
                catch {
                    response = await studentService.getAllByTutorUserId(staffId)
                }

                if(!isActual){
                    return
                }

                const nextStudentItems = response.students.map(student => ({
                    id: student.id,
                    name: `${student.id}. ${[student.first_name, student.last_name, student.middle_name].filter(Boolean).join(' ').trim()}`,
                }))

                setStudentItems(nextStudentItems)
                setSelectedStudentIds(prev => prev.filter(id => (
                    id === TUTOR_HISTORY_FILTER_ID || nextStudentItems.some(item => item.id === id)
                )))
            }
            catch(error){
                if(isActual){
                    setStudentItems([])
                    setSelectedStudentIds([])
                    handleError(error, 'Ошибка при получении списка студентов исполнителя')
                }
            }
            finally{
                if(isActual){
                    setIsStudentsLoading(false)
                }
            }
        }

        void loadStudents()

        return () => {
            isActual = false
        }
    }, [canReadScheduleHistory, handleError, isLoading, isTutorStaff, staffId])

    useEffect(() => {
        if(!staffId || isLoading || !isTutorStaff || !canReadScheduleHistory){
            setItems([])
            return
        }

        let isActual = true
        setIsHistoryLoading(true)

        const hasTutorFilter = selectedStudentIds.includes(TUTOR_HISTORY_FILTER_ID)
        const studentIds = hasTutorFilter
            ? []
            : selectedStudentIds

        auditService.listTutorRegularScheduleHistory(staffId, studentIds)
            .then(response => {
                if(isActual){
                    if(!selectedStudentIds.length){
                        setItems(response)
                        return
                    }

                    const selectedStudentIdSet = new Set(selectedStudentIds)
                    setItems(response.filter(item => (
                        item.entity_type === 'REGULAR_SCHEDULE_HISTORY_ENTITY_TYPE_TUTOR'
                            ? hasTutorFilter
                            : selectedStudentIdSet.has(Number(item.student?.id))
                    )))
                }
            })
            .catch(error => {
                if(isActual){
                    handleError(error, 'Ошибка при получении истории изменений')
                }
            })
            .finally(() => {
                if(isActual){
                    setIsHistoryLoading(false)
                }
            })

        return () => {
            isActual = false
        }
    }, [canReadScheduleHistory, handleError, isLoading, isTutorStaff, selectedStudentIds, staffId])

    return (
        <StaffDetailLayout
            staffId={staffId}
            isLoading={(Boolean(staffId) && isLoading) || isHistoryLoading}
            loadError={loadError}
            selectedTab={3}
            showSwitch
            showPaymentDetailsTab={isTutorStaff}
            showHistoryTab={isTutorStaff && canReadScheduleHistory}
        >
            <section className={idmClasses.panel}>
                {isTutorStaff && canReadScheduleHistory
                    ? <>
                        {hasStudentFilter && (
                            <section className={staffClasses.filterItem} style={{marginBottom: 20, width: '100%', maxWidth: 560}}>
                                <section className={staffClasses.filterLabel}>Фильтр по студентам</section>
                                <DropDownListSelected
                                    isLoading={isStudentsLoading}
                                    items={historyFilterItems}
                                    selectedIdItems={selectedStudentIds}
                                    onSelected={onSelectedStudent}
                                    onSelectAll={onSelectAllStudents}
                                    placeholder="Выберите студентов"
                                    search
                                    searchPlaceholder="Поиск по студентам"
                                    emptyText="Варианты не найдены"
                                >
                                    {!isStudentsLoading && Boolean(selectedStudentItems.length) && (
                                        <section className={idmClasses.headerMeta}>
                                            {selectedStudentItems.map(item => (
                                                <span key={item.id} className={idmClasses.legacyBadge}>
                                                    {item.name}
                                                </span>
                                            ))}
                                        </section>
                                    )}
                                </DropDownListSelected>
                            </section>
                        )}
                        <AuditHistoryList
                            items={items}
                            emptyText="История изменений графика исполнителя пока пуста"
                        />
                    </>
                    : !canReadScheduleHistory
                        ? <section className={idmClasses.placeholder}>
                            История изменений недоступна
                        </section>
                    : <section className={idmClasses.placeholder}>
                        История изменений доступна только для сотрудников с ролью исполнителя
                    </section>
                }
            </section>
        </StaffDetailLayout>
    )
}
